import React, { useState, useEffect } from 'react';
import { referralAdminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { 
  Share2, Users, Link as LinkIcon, DollarSign, Plus, Edit2, Trash2, 
  Loader2, Send, Mail, MessageSquare, Check, X, Eye, Copy, 
  TrendingUp, Clock, CheckCircle, XCircle, Search, RefreshCw,
  Percent, User, ExternalLink, Phone, Trophy, Medal, Award, MessageCircle,
  Upload, FileSpreadsheet
} from 'lucide-react';

export default function ReferralCampaignsTab({ onNavigateToMessaging }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Settings
  const [settings, setSettings] = useState({ referral_enabled: false, enabled_tours: [] });
  
  // Agents
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [agentForm, setAgentForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [savingAgent, setSavingAgent] = useState(false);
  
  // Bulk Import
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkImportData, setBulkImportData] = useState('');
  const [bulkAutoApprove, setBulkAutoApprove] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportResults, setBulkImportResults] = useState(null);
  
  // Tours
  const [tours, setTours] = useState([]);
  
  // Links
  const [links, setLinks] = useState([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({ agent_id: '', tour_id: '', commission_type: 'percentage', commission_value: 10 });
  const [savingLink, setSavingLink] = useState(false);
  
  // Send dialog
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendingLink, setSendingLink] = useState(null);
  const [sendMethod, setSendMethod] = useState('email');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Conversions
  const [conversions, setConversions] = useState([]);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsRes, agentsRes, toursRes, linksRes, conversionsRes] = await Promise.all([
        referralAdminAPI.getSettings(),
        referralAdminAPI.getAgents(),
        referralAdminAPI.getTours(),
        referralAdminAPI.getLinks(),
        referralAdminAPI.getConversions(),
      ]);
      
      setSettings(settingsRes.data || { referral_enabled: false, enabled_tours: [] });
      setAgents(agentsRes.data || []);
      setTours(toursRes.data || []);
      setLinks(linksRes.data || []);
      setConversions(conversionsRes.data || []);
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const toggleReferralEnabled = async () => {
    try {
      await referralAdminAPI.updateSettings({ referral_enabled: !settings.referral_enabled });
      setSettings(prev => ({ ...prev, referral_enabled: !prev.referral_enabled }));
      toast.success(`Referral program ${!settings.referral_enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const toggleTourReferral = async (tourId) => {
    try {
      const res = await referralAdminAPI.toggleTourReferral(tourId);
      setTours(prev => prev.map(t => 
        t.id === tourId ? { ...t, referral_enabled: res.data.action === 'enabled' } : t
      ));
      setSettings(prev => ({ ...prev, enabled_tours: res.data.enabled_tours }));
      toast.success(`Tour ${res.data.action} for referrals`);
    } catch (error) {
      toast.error('Failed to toggle tour');
    }
  };

  // Agent CRUD
  const saveAgent = async () => {
    if (!agentForm.name || !agentForm.email || !agentForm.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!selectedAgent && !agentForm.password) {
      toast.error('Password is required for new agents');
      return;
    }
    
    setSavingAgent(true);
    try {
      if (selectedAgent) {
        await referralAdminAPI.updateAgent(selectedAgent.id, agentForm);
        toast.success('Agent updated');
      } else {
        await referralAdminAPI.createAgent(agentForm);
        toast.success('Agent created');
      }
      setShowAgentDialog(false);
      setAgentForm({ name: '', email: '', phone: '', password: '' });
      setSelectedAgent(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save agent');
    } finally {
      setSavingAgent(false);
    }
  };

  const deleteAgent = async (agent) => {
    if (!window.confirm(`Delete agent "${agent.name}"? This will also delete all their referral links.`)) return;
    
    try {
      await referralAdminAPI.deleteAgent(agent.id);
      toast.success('Agent deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const approveAgent = async (agent) => {
    try {
      await referralAdminAPI.approveAgent(agent.id);
      toast.success('Agent approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve agent');
    }
  };

  // Link CRUD
  const saveLink = async () => {
    if (!linkForm.agent_id || !linkForm.tour_id) {
      toast.error('Please select an agent and tour');
      return;
    }
    
    setSavingLink(true);
    try {
      await referralAdminAPI.createLink(linkForm);
      toast.success('Referral link created');
      setShowLinkDialog(false);
      setLinkForm({ agent_id: '', tour_id: '', commission_type: 'percentage', commission_value: 10 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create link');
    } finally {
      setSavingLink(false);
    }
  };

  const deleteLink = async (link) => {
    if (!window.confirm('Delete this referral link?')) return;
    
    try {
      await referralAdminAPI.deleteLink(link.id);
      toast.success('Link deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  // Bulk Import
  const handleBulkImport = async () => {
    if (!bulkImportData.trim()) {
      toast.error('Please paste CSV data');
      return;
    }
    
    setBulkImporting(true);
    setBulkImportResults(null);
    
    try {
      // Parse CSV data
      const lines = bulkImportData.trim().split('\n');
      const agents = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Skip header row if detected
        if (i === 0 && (line.toLowerCase().includes('name') || line.toLowerCase().includes('email'))) {
          continue;
        }
        
        const parts = line.split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
        if (parts.length >= 2) {
          agents.push({
            name: parts[0],
            email: parts[1],
            phone: parts[2] || ''
          });
        }
      }
      
      if (agents.length === 0) {
        toast.error('No valid agent data found');
        setBulkImporting(false);
        return;
      }
      
      const res = await referralAdminAPI.bulkImportAgents({
        agents,
        auto_approve: bulkAutoApprove,
        generate_password: true
      });
      
      setBulkImportResults(res.data);
      toast.success(`Imported ${res.data.success?.length || 0} agents`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import agents');
    } finally {
      setBulkImporting(false);
    }
  };

  const sendReferralLink = async () => {
    if (!sendingLink) return;
    
    setSending(true);
    try {
      const res = await referralAdminAPI.sendLink({
        link_id: sendingLink.id,
        method: sendMethod,
        custom_message: customMessage || null,
      });
      
      if (res.data.whatsapp_url) {
        window.open(res.data.whatsapp_url, '_blank');
      }
      
      toast.success(`Link sent via ${sendMethod}`);
      setShowSendDialog(false);
      setSendingLink(null);
      setCustomMessage('');
    } catch (error) {
      toast.error('Failed to send link');
    } finally {
      setSending(false);
    }
  };

  const copyLink = (link) => {
    const url = `${window.location.origin}/r/${link.code}/${link.tour_id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  // Open Messaging Center with referral link email pre-filled
  const sendReferralLinkViaMessaging = (link, agent) => {
    if (!onNavigateToMessaging) {
      toast.error('Messaging navigation not available');
      return;
    }
    
    const referralUrl = `${window.location.origin}/r/${link.code}/${link.tour_id}`;
    const tour = tours.find(t => t.id === link.tour_id);
    const tourName = tour?.title || 'our amazing tour';
    
    const subject = `🌴 Exclusive Tour Invitation - ${tourName}`;
    const body = `Hi there!

I wanted to share an exclusive link for an amazing tour experience with you!

🎯 **${tourName}**

Use my special referral link to book:
${referralUrl}

When you book using this link, you'll be supporting me as a referral partner.

Don't miss out on this incredible adventure!

Questions? Just reply to this email.

Best regards`;

    // Navigate to messaging center with pre-filled content
    onNavigateToMessaging('', 'Potential Customer', subject, body);
    toast.success('Opening Messaging Center with referral link...');
  };

  // Share referral link via WhatsApp
  const shareReferralLinkWhatsApp = (link) => {
    const referralUrl = `${window.location.origin}/r/${link.code}/${link.tour_id}`;
    const tour = tours.find(t => t.id === link.tour_id);
    const tourName = tour?.title || 'an amazing tour';
    
    const message = `🌴 *Exclusive Tour Invitation!*

Check out this amazing tour: *${tourName}*

Book using my special link:
${referralUrl}

Don't miss out! 🎉`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const updateConversionStatus = async (conversionId, status) => {
    try {
      await referralAdminAPI.updateConversionStatus(conversionId, status);
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Filter agents
  const filteredAgents = agents.filter(a => 
    !searchTerm || 
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const totalEarnings = conversions.reduce((sum, c) => sum + (c.commission_amount || 0), 0);
  const pendingPayouts = conversions.filter(c => c.status === 'pending').reduce((sum, c) => sum + (c.commission_amount || 0), 0);
  const approvedPayouts = conversions.filter(c => c.status === 'payout_approved').reduce((sum, c) => sum + (c.commission_amount || 0), 0);

  // Leaderboard - Calculate agent rankings
  const agentLeaderboard = agents.map(agent => {
    const agentConversions = conversions.filter(c => c.agent_id === agent.id);
    const totalCommission = agentConversions.reduce((sum, c) => sum + (c.commission_amount || 0), 0);
    const totalBookings = agentConversions.length;
    const agentLinks = links.filter(l => l.agent_id === agent.id);
    const totalClicks = agentLinks.reduce((sum, l) => sum + (l.clicks || 0), 0);
    
    return {
      ...agent,
      total_commission: totalCommission,
      total_bookings: totalBookings,
      total_clicks: totalClicks,
      conversion_rate: totalClicks > 0 ? ((totalBookings / totalClicks) * 100).toFixed(1) : 0
    };
  })
  .filter(a => a.status === 'approved')
  .sort((a, b) => b.total_commission - a.total_commission);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referral-campaigns-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Referral Campaigns</h2>
          <p className="text-muted-foreground">Manage referral agents and track commissions</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Referral Program</Label>
            <Switch
              checked={settings.referral_enabled}
              onCheckedChange={toggleReferralEnabled}
              data-testid="referral-toggle"
            />
            <Badge variant={settings.referral_enabled ? "default" : "secondary"}>
              {settings.referral_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Agents</p>
              <p className="text-2xl font-bold">{agents.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Earnings</p>
              <p className="text-2xl font-bold">${totalEarnings.toFixed(2)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending Payout</p>
              <p className="text-2xl font-bold">${pendingPayouts.toFixed(2)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Approved Payout</p>
              <p className="text-2xl font-bold">${approvedPayouts.toFixed(2)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-5 w-fit">
          <TabsTrigger value="overview" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="gap-2">
            <Trophy className="h-4 w-4" />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="agents" className="gap-2">
            <Users className="h-4 w-4" />
            Agents
          </TabsTrigger>
          <TabsTrigger value="tours" className="gap-2">
            <Share2 className="h-4 w-4" />
            Tours
          </TabsTrigger>
          <TabsTrigger value="conversions" className="gap-2">
            <DollarSign className="h-4 w-4" />
            Conversions
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="space-y-6">
            {/* Recent Links */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">Referral Links</h3>
                <Button onClick={() => setShowLinkDialog(true)} size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Link
                </Button>
              </div>
              
              {links.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No referral links created yet</p>
              ) : (
                <div className="space-y-3">
                  {links.slice(0, 10).map((link) => (
                    <div key={link.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{link.agent_name}</span>
                          <span className="text-muted-foreground">→</span>
                          <span>{link.tour_name}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <LinkIcon className="h-3 w-3" />
                            {link.code}
                          </span>
                          <span className="flex items-center gap-1">
                            {link.commission_type === 'percentage' ? <Percent className="h-3 w-3" /> : <DollarSign className="h-3 w-3" />}
                            {link.commission_type === 'percentage' ? `${link.commission_value}%` : `$${link.commission_value}`}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {link.clicks || 0} clicks
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            {link.conversion_count || 0} conversions
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => copyLink(link)} title="Copy Link">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => sendReferralLinkViaMessaging(link, selectedAgent)}
                          className="gap-1"
                          title="Send via Email"
                        >
                          <Mail className="h-4 w-4" />
                          Email
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => shareReferralLinkWhatsApp(link)}
                          className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Share via WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4" />
                          WhatsApp
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteLink(link)} className="text-red-600" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="mt-6">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Top Performing Agents
                </h3>
                <p className="text-sm text-muted-foreground">Ranked by total commission earned</p>
              </div>
              <Button variant="outline" onClick={loadData} size="sm" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
            
            {agentLeaderboard.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No agents with conversions yet</p>
                <p className="text-sm text-muted-foreground">Agents will appear here once they generate bookings</p>
              </div>
            ) : (
              <div className="space-y-3">
                {agentLeaderboard.map((agent, index) => {
                  const rank = index + 1;
                  const isTop3 = rank <= 3;
                  const medalColors = {
                    1: 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white',
                    2: 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800',
                    3: 'bg-gradient-to-r from-amber-600 to-amber-700 text-white'
                  };
                  
                  return (
                    <div 
                      key={agent.id} 
                      className={`flex items-center gap-4 p-4 rounded-lg border ${isTop3 ? 'bg-gradient-to-r from-amber-50/50 to-transparent' : 'bg-muted/30'}`}
                      data-testid={`leaderboard-row-${rank}`}
                    >
                      {/* Rank Badge */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${isTop3 ? medalColors[rank] : 'bg-muted text-muted-foreground'}`}>
                        {isTop3 ? (
                          rank === 1 ? <Medal className="h-5 w-5" /> : rank
                        ) : rank}
                      </div>
                      
                      {/* Agent Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{agent.name}</span>
                          {rank === 1 && <Badge className="bg-amber-500 text-white text-xs">Top Performer</Badge>}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {agent.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {agent.total_clicks} clicks
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            {agent.total_bookings} bookings
                          </span>
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {agent.conversion_rate}% rate
                          </span>
                        </div>
                      </div>
                      
                      {/* Commission */}
                      <div className="text-right">
                        <p className={`text-xl font-bold ${isTop3 ? 'text-amber-600' : 'text-green-600'}`}>
                          ${agent.total_commission.toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">Total Earned</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Summary Stats */}
            {agentLeaderboard.length > 0 && (
              <div className="mt-6 pt-6 border-t grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{agentLeaderboard.length}</p>
                  <p className="text-xs text-muted-foreground">Active Agents</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    ${agentLeaderboard.reduce((sum, a) => sum + a.total_commission, 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Commissions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {agentLeaderboard.reduce((sum, a) => sum + a.total_bookings, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Bookings</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {agentLeaderboard.reduce((sum, a) => sum + a.total_clicks, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Clicks</p>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Agents Tab */}
        <TabsContent value="agents" className="mt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => { setBulkImportData(''); setBulkImportResults(null); setShowBulkImportDialog(true); }} className="gap-2">
                  <Upload className="h-4 w-4" />
                  Bulk Import
                </Button>
                <Button onClick={() => { setSelectedAgent(null); setAgentForm({ name: '', email: '', phone: '', password: '' }); setShowAgentDialog(true); }} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Agent
                </Button>
              </div>
            </div>

            {filteredAgents.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No referral agents yet</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredAgents.map((agent) => (
                  <Card key={agent.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{agent.name}</span>
                            {!agent.is_approved && (
                              <Badge variant="secondary" className="text-xs">Pending Approval</Badge>
                            )}
                            {!agent.is_active && (
                              <Badge variant="destructive" className="text-xs">Inactive</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{agent.email}</span>
                            <span>{agent.phone}</span>
                            <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{agent.referral_code}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Earnings</p>
                          <p className="font-semibold text-green-600">${(agent.total_earnings || 0).toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Bookings</p>
                          <p className="font-semibold">{agent.total_bookings || 0}</p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {!agent.is_approved && (
                            <Button variant="outline" size="sm" onClick={() => approveAgent(agent)} className="gap-1 text-green-600">
                              <Check className="h-4 w-4" />
                              Approve
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => { setSelectedAgent(agent); setAgentForm({ name: agent.name, email: agent.email, phone: agent.phone, password: '' }); setShowAgentDialog(true); }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteAgent(agent)} className="text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tours Tab */}
        <TabsContent value="tours" className="mt-6">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-semibold text-lg">Tours Available for Referral</h3>
                <p className="text-sm text-muted-foreground">Enable tours that referral agents can promote</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadData} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
            
            <div className="space-y-2">
              {tours.map((tour) => (
                <div key={tour.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {tour.images?.[0] && (
                      <img src={tour.images[0]} alt="" className="w-12 h-12 rounded object-cover" />
                    )}
                    <div>
                      <p className="font-medium">{tour.title}</p>
                      <p className="text-sm text-muted-foreground">${tour.price} per person</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant={tour.referral_enabled ? "default" : "secondary"}>
                      {tour.referral_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Switch
                      checked={tour.referral_enabled}
                      onCheckedChange={() => toggleTourReferral(tour.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Conversions Tab */}
        <TabsContent value="conversions" className="mt-6">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-semibold text-lg">Referral Conversions</h3>
                <p className="text-sm text-muted-foreground">Track bookings from referral links</p>
              </div>
            </div>
            
            {conversions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No conversions yet</p>
            ) : (
              <div className="space-y-3">
                {conversions.map((conv) => (
                  <div key={conv.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{conv.agent_name}</span>
                        <span className="text-muted-foreground">→</span>
                        <span>{conv.booker_name}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {conv.tour_name} • {new Date(conv.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-green-600">${conv.commission_amount?.toFixed(2)}</p>
                      </div>
                      
                      <Select 
                        value={conv.status} 
                        onValueChange={(val) => updateConversionStatus(conv.id, val)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">
                            <span className="flex items-center gap-2">
                              <Clock className="h-3 w-3 text-amber-500" />
                              Pending
                            </span>
                          </SelectItem>
                          <SelectItem value="payout_approved">
                            <span className="flex items-center gap-2">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              Payout Approved
                            </span>
                          </SelectItem>
                          <SelectItem value="paid">
                            <span className="flex items-center gap-2">
                              <DollarSign className="h-3 w-3 text-blue-500" />
                              Paid
                            </span>
                          </SelectItem>
                          <SelectItem value="cancelled">
                            <span className="flex items-center gap-2">
                              <XCircle className="h-3 w-3 text-red-500" />
                              Cancelled
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Agent Dialog */}
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedAgent ? 'Edit Agent' : 'Create Referral Agent'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={agentForm.name}
                onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={agentForm.email}
                onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                value={agentForm.phone}
                onChange={(e) => setAgentForm({ ...agentForm, phone: e.target.value })}
                placeholder="+1 787 555 1234"
              />
            </div>
            {!selectedAgent && (
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={agentForm.password}
                  onChange={(e) => setAgentForm({ ...agentForm, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAgentDialog(false)}>Cancel</Button>
            <Button onClick={saveAgent} disabled={savingAgent}>
              {savingAgent && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {selectedAgent ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Referral Link</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Agent *</Label>
              <Select value={linkForm.agent_id} onValueChange={(val) => setLinkForm({ ...linkForm, agent_id: val })}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.filter(a => a.is_approved && a.is_active).map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Select Tour *</Label>
              <Select value={linkForm.tour_id} onValueChange={(val) => setLinkForm({ ...linkForm, tour_id: val })}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose tour" />
                </SelectTrigger>
                <SelectContent>
                  {tours.filter(t => t.referral_enabled).map((tour) => (
                    <SelectItem key={tour.id} value={tour.id}>{tour.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Commission Type</Label>
                <Select value={linkForm.commission_type} onValueChange={(val) => setLinkForm({ ...linkForm, commission_type: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Commission Value</Label>
                <Input
                  type="number"
                  value={linkForm.commission_value}
                  onChange={(e) => setLinkForm({ ...linkForm, commission_value: parseFloat(e.target.value) || 0 })}
                  min={0}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
            <Button onClick={saveLink} disabled={savingLink}>
              {savingLink && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Link Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Referral Link</DialogTitle>
          </DialogHeader>
          
          {sendingLink && (
            <div className="space-y-4 py-4">
              <Card className="p-4 bg-muted/50">
                <p className="text-sm"><strong>Agent:</strong> {sendingLink.agent_name}</p>
                <p className="text-sm"><strong>Tour:</strong> {sendingLink.tour_name}</p>
                <p className="text-sm font-mono mt-2">{window.location.origin}/r/{sendingLink.code}/{sendingLink.tour_id}</p>
              </Card>
              
              <div>
                <Label>Send Via</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant={sendMethod === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendMethod('email')}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </Button>
                  <Button
                    type="button"
                    variant={sendMethod === 'sms' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendMethod('sms')}
                    className="gap-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    SMS
                  </Button>
                  <Button
                    type="button"
                    variant={sendMethod === 'whatsapp' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendMethod('whatsapp')}
                    className="gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    WhatsApp
                  </Button>
                </div>
              </div>
              
              <div>
                <Label>Custom Message (Optional)</Label>
                <Input
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Add a personal note..."
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancel</Button>
            <Button onClick={sendReferralLink} disabled={sending} className="gap-2">
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              <Send className="h-4 w-4" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Bulk Import Agents
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium mb-2">CSV Format:</p>
              <code className="text-xs bg-blue-100 px-2 py-1 rounded block">
                name,email,phone<br/>
                John Doe,john@example.com,+1234567890<br/>
                Jane Smith,jane@example.com,+0987654321
              </code>
              <p className="text-xs text-blue-700 mt-2">Phone is optional. Headers are optional.</p>
            </div>

            <div>
              <Label>Paste CSV Data</Label>
              <textarea
                value={bulkImportData}
                onChange={(e) => setBulkImportData(e.target.value)}
                placeholder="Paste your CSV data here..."
                className="w-full h-40 p-3 border rounded-lg text-sm font-mono resize-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={bulkAutoApprove}
                onCheckedChange={setBulkAutoApprove}
              />
              <Label>Auto-approve agents (skip manual approval)</Label>
            </div>

            {bulkImportResults && (
              <div className="border rounded-lg p-4 space-y-2">
                <p className="font-medium">Import Results:</p>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600">✓ {bulkImportResults.success?.length || 0} imported</span>
                  <span className="text-red-600">✗ {bulkImportResults.failed?.length || 0} failed</span>
                </div>
                
                {bulkImportResults.success?.length > 0 && (
                  <div className="bg-green-50 p-3 rounded text-sm max-h-32 overflow-auto">
                    <p className="font-medium text-green-800 mb-1">Imported Agents:</p>
                    {bulkImportResults.success.map((a, i) => (
                      <div key={i} className="text-green-700">
                        {a.name} ({a.email}) - Code: <code className="font-mono">{a.referral_code}</code>
                        {a.temp_password && <span className="text-green-600"> - Temp Pass: {a.temp_password}</span>}
                      </div>
                    ))}
                  </div>
                )}
                
                {bulkImportResults.failed?.length > 0 && (
                  <div className="bg-red-50 p-3 rounded text-sm max-h-32 overflow-auto">
                    <p className="font-medium text-red-800 mb-1">Failed:</p>
                    {bulkImportResults.failed.map((f, i) => (
                      <div key={i} className="text-red-700">{f.email}: {f.reason}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkImportDialog(false)}>
              {bulkImportResults ? 'Close' : 'Cancel'}
            </Button>
            {!bulkImportResults && (
              <Button onClick={handleBulkImport} disabled={bulkImporting} className="gap-2">
                {bulkImporting && <Loader2 className="h-4 w-4 animate-spin" />}
                <Upload className="h-4 w-4" />
                Import Agents
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
